import React, { useCallback } from 'react'

import { Grid, MenuItem, TextField } from '@material-ui/core'
import { ErrorBoundary } from '@scoir/error-reporter'
import { get, isEmpty, map } from 'lodash'
import PropTypes from 'prop-types'

import InternationalPhoneField from './InternationalPhoneField/index.jsx'
import { isCountryCodeValid } from './validation.js'

const VALID_PHONE_TYPES = ['Cell', 'Home', 'Work']

export const errorMessages = {
  invalid: 'Phone number invalid',
  combo: 'This field is required if you choose to share a phone number',
}

export const getPhoneErrorMessage = (showRequiredErrors, phone) => {
  const msg = isEmpty(get(phone, 'Number')) ? errorMessages.combo : errorMessages.invalid
  return showRequiredErrors && msg
}

export const getTypeErrorMessage = (showRequiredErrors) => {
  return showRequiredErrors ? errorMessages.combo : ''
}

export const getFieldLabel = (label, required) => {
  return `${label}${required ? '' : ' (Optional)'}`
}

export const onPhoneChange = (onChange, updateValidation) => {
  return (numberWithoutCountryCode, countryCode, isValid) => {
    onChange({
      Number: numberWithoutCountryCode,
      CountryCode: countryCode,
    })
    updateValidation({
      Number: !isValid,
      CountryCode: !isCountryCodeValid(countryCode),
    })
  }
}

export const useMinimalErrorValues = (validation) =>
  useCallback(
    (fieldName, errorMessage) => {
      const answer = get(validation, fieldName)
      if (errorMessage) {
        return answer ? errorMessage : undefined
      }
      return answer
    },
    [validation],
  )

const PhoneForm = ({
  disabled = false,
  phone,
  onChange,
  getErrorValue,
  requiresPhoneFields = true,
  showRequiredErrors = false,
  updateValidation,
  showTypeField = true,
  label = 'Phone Number',
}) => {
  const phoneErrorMessage = getPhoneErrorMessage(showRequiredErrors, phone)
  const typeError = getErrorValue('Type', getTypeErrorMessage(showRequiredErrors))

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        container
        item
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          <ErrorBoundary>
            <InternationalPhoneField
              disabled={disabled}
              countryCode={get(phone, 'CountryCode')}
              label={getFieldLabel(label, requiresPhoneFields)}
              phone={get(phone, 'Number')}
              inputProps={{
                error: getErrorValue('Number'),
                helperText: getErrorValue('Number', phoneErrorMessage),
              }}
              onChange={onPhoneChange(onChange, updateValidation)}
            />
          </ErrorBoundary>
        </Grid>
      </Grid>
      {showTypeField && (
        <Grid
          container
          item
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <TextField
              disabled={disabled}
              select
              label={getFieldLabel('Phone Type', requiresPhoneFields)}
              id="phone-type"
              value={get(phone, 'Type', '')}
              error={getErrorValue('Type')}
              helperText={typeError}
              onChange={(e) => {
                onChange({ Type: e.target.value })
              }}
              variant="outlined"
              fullWidth
            >
              <MenuItem value="">Select a type</MenuItem>
              {map(VALID_PHONE_TYPES, (type) => (
                <MenuItem
                  key={type}
                  value={type}
                >
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

PhoneForm.propTypes = {
  phone: PropTypes.shape({
    CountryCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Number: PropTypes.string,
    Type: PropTypes.string,
  }).isRequired,
  validation: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeValidation: PropTypes.func,
  getErrorValue: PropTypes.func,
  updateValidation: PropTypes.func,
  requiresPhoneFields: PropTypes.bool,
  phoneErrorMsg: PropTypes.string,
  showRequiredErrors: PropTypes.bool,
  disabled: PropTypes.bool,
  showTypeField: PropTypes.bool,
  label: PropTypes.string,
}
export default PhoneForm
